import Component from '@glimmer/component'
import { min, max, addDays, isSameDay, differenceInCalendarDays } from 'date-fns'
import { assert } from '@ember/debug'
import { hash } from '@ember/helper'

interface Signature {
  Element: HTMLElement
  Args: {
    startDate: Date
    endDate?: Date | null // optional, you can use this component as a single date picker if you like!
    maxDays?: number
    onDateSelect?: ({ startDate, endDate }: { startDate: Date; endDate?: Date | null | undefined }) => unknown
  }
  Blocks: {
    default: [
      {
        start: { onDateSelect: (date: Date) => void; min: Date }
        end: { onDateSelect: (date: Date) => void; min: Date; max: Date }
        durationDays: number | null
        setRelativeDays: (daysOrEvent: number | Event) => void
        setAbsoluteDays: (daysOrEvent: number | Event) => void
      },
    ]
    abc: [string]
  }
}

/**
 * This is a barebones date component. This component is responsible for handling date logic like;
 * - end date must be on or after start date + 1 & today
 * - updating dates when duration changes
 * - min/max date. min date always "today". max date should be never, but allows a max duration to be given (eg 365 days)
 *
 * Can also be used as a single date picker control - just dont use/include the end date or duration.
 *
 * Using this component, you can add your own inputs, labels, buttons, styles, themes...
 *
 * Usage:
 * <TwoDatePicker>
 *   @startDate={{this.myStartDate}}
 *   @endDate={{this.myEndDate}}
 *   @maxDays={{365}}
 *   @onDateSelect={{this.onDateSelect}}
 *   as |twoDate|
 * >
 *   Start Date: {{this.myStartDate}}
 *   End Date: {{this.myEndDate}}
 *   <TwoDatePickerInput @date={{this.myStartDate}} @onDateSelect={{twoDate.start.onDateSelect}} @min={{twoDate.start.min}} />
 *   <TwoDatePickerInput @date={{this.myEndDate}} @onDateSelect={{twoDate.end.onDateSelect}} @min={{twoDate.end.min}} @max={{twoDate.end.max}} />
 *   <button {{on "click" (fn twoDate.setAbsoluteDays 7)}}>1 week</button>
 *   <input type="number" value={{twoDate.durationDays}} {{on "input" twoDate.setAbsoluteDays}} >
 * </TwoDatePicker>
 */
export class TwoDatePicker extends Component<Signature> {
  get duration() {
    const { startDate, endDate } = this.args

    if (startDate && endDate) {
      return differenceInCalendarDays(endDate, startDate)
    }

    return null
  }

  get maxDays() {
    return this.args.maxDays ?? 365
  }

  /** smallest of today vs start date */
  get minStartDate() {
    const today = new Date()
    return min([this.args.startDate, today])
  }

  get minEndDate() {
    return this.endDateBounds(this.args.startDate).minEndDate
  }

  get maxEndDate() {
    return this.endDateBounds(this.args.startDate).maxEndDate
  }

  /**
   * Calculates end date bounds based on a given start date.
   * Needs to be variable start because we don't always want to use the start date from component args
   */
  endDateBounds(startDate: Date) {
    // end date selection must be at least on or after; the day after startdate, and today
    const today = new Date()
    const dayAfterStartDate = addDays(startDate, 1)
    const minEndDate = max([dayAfterStartDate, today])

    const maxEndDate = addDays(startDate, this.maxDays)
    return { minEndDate, maxEndDate }
  }

  /**
   * Bound end date by given start date
   */
  boundEndDate(startDate: Date, endDate: Date) {
    const bounds = this.endDateBounds(startDate)
    if (isSameDay(endDate, bounds.minEndDate) || endDate < bounds.minEndDate) {
      return bounds.minEndDate
    }
    if (isSameDay(endDate, bounds.maxEndDate) || endDate > bounds.maxEndDate) {
      return bounds.maxEndDate
    }
    return endDate
  }

  onStartDateSelect = (startDate: Date) => {
    const { endDate } = this.args
    const boundedEndDate = endDate ? this.boundEndDate(startDate, endDate) : endDate
    // When a start date is selected, always ensure the min/max end date rules are upheld
    this.args.onDateSelect?.({ startDate, endDate: boundedEndDate })
  }

  onEndDateSelect = (endDate: Date) => {
    this.args.onDateSelect?.({ startDate: this.args.startDate, endDate })
  }

  /** Sets end date to exactly this number of days from the start date */
  setAbsoluteDays = (daysOrEvent: number | Event) => {
    const days = this.parseDaysOrEvent(daysOrEvent)
    this.setDays(days, false)
  }

  /** Changes end date, based on the relative value given (+7 -> add 7 days, -7 -> minus 7 days) */
  setRelativeDays = (daysOrEvent: number | Event) => {
    const days = this.parseDaysOrEvent(daysOrEvent)
    this.setDays(days, true)
  }

  parseDaysOrEvent = (daysOrEvent: number | Event): number => {
    let days: number
    if (typeof daysOrEvent === 'number') {
      days = daysOrEvent
    } else {
      assert('event target for setting days must be input element', daysOrEvent.target instanceof HTMLInputElement)
      days = parseInt(daysOrEvent.target.value, 10)
    }
    return days
  }

  /** Sets end date based on the number of days given. Relative days will adjust the end date +/- that number of days.
   * Else exact will set end date to exactly that number of days from start date
   */
  setDays = (days: number, isRelative: boolean) => {
    const { startDate, endDate } = this.args
    if (Number.isInteger(days) && endDate) {
      const desiredEndDate = addDays(isRelative ? endDate : startDate, days)
      this.args.onDateSelect?.({
        startDate,
        endDate: this.boundEndDate(startDate, desiredEndDate),
      })
    }
  }

  <template>
    {{yield
      (hash
        start=(hash onDateSelect=this.onStartDateSelect min=this.minStartDate)
        end=(hash onDateSelect=this.onEndDateSelect min=this.minEndDate max=this.maxEndDate)
        durationDays=this.duration
        setRelativeDays=this.setRelativeDays
        setAbsoluteDays=this.setAbsoluteDays
      )
    }}
  </template>
}
